import { useEffect, useId, useMemo, useRef, useState } from "react";
import { usePosicionDesplegable } from "../posicionDesplegable";
import Icon from "../Icon";
import * as api from "../../api";
import { HD_PRIOS } from "../../constants";
import { etapaES } from "../../utils";

// Enlaza un cronometro con una INCIDENCIA del panel de asistencia de Odoo.
//
// Es el tercer hermano de SelectorPaso y NotionSelector, y existe porque es el
// caso que motiva todo el cronometro: entra una incidencia, se pausa lo que
// habia y se atiende. Escribe incidencia_id / incidencia_ref.
//
// A diferencia de sus hermanos los datos NO llegan por prop: las incidencias se
// leen de Odoo en vivo y una copia local envejeceria mal. Se piden al primer
// foco y se quedan en cache durante la sesion, porque Odoo tarda lo suyo y esto
// se abre varias veces al dia.
let cache = null;
// Las cerradas recientes (sin buscar), aparte: solo se piden si se enciende «Incluir cerradas».
let cacheCierres = null;

// «12 oct». Una incidencia cerrada dice cuando: exacto si Odoo guarda la fecha de cierre, «hacia» si es la de la ultima
// modificacion (cierre_exacto false).
const diaMes = iso => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("es-ES", { day: "numeric", month: "short" }).replace(".", "");
};
export function textoCierre(x) {
  const d = diaMes(x?.cerrado_el);
  if (!d) return "Cerrada";
  return x.cierre_exacto === false ? `Cerrada hacia el ${d}` : `Cerrada el ${d}`;
}

export default function SelectorIncidencia({ ticket, onChange, onAbrirPanel }) {
  const [abierto,  setAbierto]  = useState(false);
  const [q,        setQ]        = useState("");
  const [tickets,  setTickets]  = useState(cache);
  const [cargando, setCargando] = useState(false);
  const [fallo,    setFallo]    = useState(null);
  const [cerradas, setCerradas] = useState(false);       // «Incluir cerradas»
  const [cierres,  setCierres]  = useState(cacheCierres);   // { tickets, total, dias }
  const [cargandoC, setCargandoC] = useState(false);
  const [falloC,   setFalloC]   = useState(false);
  const [activo,   setActivo]   = useState(0);          // opcion marcada con el teclado
  const id = useId();
  const ref = useRef(null);
  const panelRef = useRef(null);
  const { estilo: posicion } = usePosicionDesplegable(abierto, ref, panelRef, { altoMax: 280 });

  useEffect(() => {
    if (!abierto) return;
    const fuera = e => {
      if (ref.current && !ref.current.contains(e.target)) { setAbierto(false); setQ(""); }
    };
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);

  // Con «Incluir cerradas» el servidor busca entre las cerradas (tope 50): se pide al encender y al escribir (con un
  // respiro, para no preguntar a Odoo por cada letra). Sin texto se reutiliza lo ya pedido.
  useEffect(() => {
    if (!cerradas || !abierto) return undefined;
    const texto = q.trim();
    if (!texto && cacheCierres) { setCierres(cacheCierres); setFalloC(false); return undefined; }
    let vivo = true;
    const id = setTimeout(async () => {
      setCargandoC(true);
      try {
        const r = await api.getHelpdeskTickets({ cerradas: 1, mine: 1, ...(texto ? { q: texto } : {}) });
        const lista = r.tickets || [];
        const datos = { tickets: lista, total: Number.isFinite(r.total) ? r.total : lista.length, dias: r.dias || 90 };
        if (!texto) cacheCierres = datos;
        if (vivo) { setCierres(datos); setFalloC(false); }
      } catch { if (vivo) setFalloC(true); }
      finally { if (vivo) setCargandoC(false); }
    }, texto ? 300 : 0);
    return () => { vivo = false; clearTimeout(id); };
  }, [cerradas, abierto, q]);

  const cargar = async () => {
    if (tickets || cargando) return;
    setCargando(true);
    try {
      const r = await api.getHelpdeskTickets({ mine: 1 });
      cache = r.tickets || [];
      setTickets(cache);
      setFallo(null);
    } catch (e) {
      // Mismo criterio que useHelpdesk: que Odoo no este conectado no es un
      // error que gritar, es un paso que falta.
      setFallo(/conectado|caducado/i.test(e.message || "") ? "sesion" : "error");
    } finally { setCargando(false); }
  };

  // Se busca por numero y por titulo: al telefono te dicen el numero, y en la
  // cabeza te acuerdas de "lo de la impresora".
  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    const base = t
      ? (tickets || []).filter(x =>
          String(x.titulo || "").toLowerCase().includes(t) ||
          String(x.numero || "").toLowerCase().includes(t))
      : (tickets || []);
    return base.slice(0, 14);
  }, [q, tickets]);

  const elegir = x => { onChange(x); setAbierto(false); setQ(""); };

  // Las opciones en orden de pantalla (abiertas y, si se piden, cerradas) para moverse con el teclado
  const listaCierres = cerradas && !falloC ? (cierres?.tickets || []) : [];
  const plano = [...filtrados, ...listaCierres];
  const teclado = e => {
    if (e.key === "ArrowDown") { e.preventDefault(); setAbierto(true); setActivo(a => Math.min(plano.length - 1, a + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActivo(a => Math.max(0, a - 1)); }
    else if (e.key === "Home" && abierto) { e.preventDefault(); setActivo(0); }
    else if (e.key === "End" && abierto) { e.preventDefault(); setActivo(Math.max(0, plano.length - 1)); }
    else if (e.key === "Enter" && abierto && plano[activo]) { e.preventDefault(); elegir(plano[activo]); }
    else if (e.key === "Escape") { e.stopPropagation(); setAbierto(false); setQ(""); }
  };

  const fila = (x, i) => {
    const prio = HD_PRIOS[String(x.prioridad)] || HD_PRIOS["0"];
    return (
      <div key={x.id} id={`${id}-op-${i}`} role="option" aria-selected={i === activo}
        onMouseDown={e => e.preventDefault()} onClick={() => elegir(x)} onMouseEnter={() => setActivo(i)}
        style={{ padding: "7px 12px", cursor: "pointer", display: "flex", minHeight: 36,
          alignItems: "center", gap: 8, fontSize: 12,
          background: i === activo ? "var(--accent-bg)" : "transparent" }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
          background: prio.color }} title={prio.label} />
        <span style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--text3)",
          flexShrink: 0 }}>#{x.numero}</span>
        <span style={{ flex: 1, fontWeight: 500, overflow: "hidden",
          textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{x.titulo}</span>
        {x.cerrada ? (
          // Texto y simbolo, no solo color: el distintivo solo vive en la lista (el chip ya enlazado no lo lleva)
          <span style={{ fontSize: 11, fontWeight: 700, flexShrink: 0, padding: "1px 7px", borderRadius: 20,
            border: "1px solid var(--ok-line)", background: "var(--ok-bg)", color: "var(--ok-text)", whiteSpace: "nowrap" }}>
            ✓ {textoCierre(x)}
          </span>
        ) : x.etapa?.nombre && (
          <span style={{ fontSize: 11, color: "var(--text3)", flexShrink: 0,
            maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis",
            whiteSpace: "nowrap" }}>{etapaES(x.etapa.nombre)}</span>
        )}
      </div>
    );
  };

  if (ticket) {
    const prio = HD_PRIOS[String(ticket.prioridad)] || HD_PRIOS["0"];
    return (
      <div title={ticket.etapa?.nombre ? etapaES(ticket.etapa.nombre) : ""}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 10px",
          borderRadius: 8, border: "1px solid var(--red-line)", background: "var(--red-bg)",
          width: "fit-content", maxWidth: "100%" }}>
        <Icon name="headset" size={11} color="var(--red-text)" />
        <span style={{ fontSize: 11, fontFamily: "var(--mono)", color: "var(--red-text)",
          flexShrink: 0 }}>#{ticket.numero}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: "var(--red-text)", flex: 1,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {ticket.titulo}
        </span>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: prio.color,
          flexShrink: 0 }} title={prio.label} />
        <button type="button" className="btn cw-x" onClick={() => onChange(null)}
          title="Quitar el enlace" aria-label={`Quitar la incidencia #${ticket.numero}`}>×</button>
      </div>
    );
  }

  // La sesion de Odoo caducada no se resuelve desde aqui: el formulario esta en
  // el widget de fichaje, que en Inicio esta justo al lado.
  if (fallo === "sesion") {
    return (
      <div style={aviso}>
        <Icon name="plug" size={11} color="var(--text3)" style={{ marginTop: 1, flexShrink: 0 }} />
        <span>Odoo no está conectado, así que no se pueden leer tus incidencias.
          Describe la interrupción y el tiempo se apunta igual.</span>
      </div>
    );
  }

  return (
    <div ref={ref} style={{ position: "relative", width: "100%" }}>
      <div style={{ position: "relative" }}>
        <Icon name="headset" size={11} color="var(--text3)"
          style={{ position: "absolute", left: 9, top: "50%",
            transform: "translateY(-50%)", pointerEvents: "none" }} />
        <input aria-label="Buscar incidencia" value={q} role="combobox" aria-expanded={abierto} aria-autocomplete="list"
          aria-controls={`${id}-ab ${id}-ce`} aria-activedescendant={abierto && plano[activo] ? `${id}-op-${activo}` : undefined}
          autoComplete="off"
          onChange={e => { setQ(e.target.value); setAbierto(true); setActivo(0); }}
          onFocus={() => { setAbierto(true); cargar(); }}
          onKeyDown={teclado}
          placeholder={cargando ? "Consultando Odoo…" : "Buscar tu incidencia por número o título…"}
          style={{ width: "100%", padding: "5px 8px 5px 26px", borderRadius: 8,
            border: "1px solid var(--border-ctl)", background: "var(--surface)",
            color: "var(--text)", fontSize: 12, fontFamily: "var(--font)" }} />
      </div>

      {abierto && (
        <div ref={panelRef} onMouseDown={e => { if (e.target.closest("[role=switch]") == null) e.preventDefault(); }} style={{ zIndex: 200,
          background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10,
          boxShadow: "var(--shadow-lg)", width: "min(440px, calc(100vw - 32px))",
          ...posicion }}>
          {/* El interruptor es un boton con role switch: se alcanza con teclado y dice su estado */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
            padding: "6px 12px", borderBottom: "1px solid var(--border2)" }}>
            <span style={{ fontSize: 11, color: "var(--text3)" }}>Elegirla no cambia nada en Odoo</span>
            <button type="button" role="switch" aria-checked={cerradas} className="btn"
              onClick={() => setCerradas(c => !c)}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 28, padding: "0 10px",
                borderRadius: 20, cursor: "pointer", fontFamily: "var(--font)", fontSize: 11.5, fontWeight: 700,
                border: "1px solid " + (cerradas ? "var(--accent)" : "var(--border-ctl)"),
                background: cerradas ? "var(--accent-bg)" : "var(--surface)",
                color: cerradas ? "var(--accent)" : "var(--text2)" }}>
              <span aria-hidden="true">{cerradas ? "☑" : "☐"}</span>Incluir cerradas
            </button>
          </div>
          {cargando && (
            <div style={vacio}>Consultando Odoo…</div>
          )}
          {!cargando && fallo === "error" && (
            <div style={vacio}>No se pudieron leer las incidencias</div>
          )}
          {!cargando && !fallo && !filtrados.length && !cerradas && (
            <div style={vacio}>
              {q.trim() ? "Sin resultados" : "No tienes incidencias asignadas"}
            </div>
          )}
          {filtrados.length > 0 && (
            <div id={`${id}-ab`} role="listbox" aria-label="Tus incidencias abiertas">{filtrados.map((x, i) => fila(x, i))}</div>
          )}
          {!filtrados.length && <div id={`${id}-ab`} role="listbox" aria-label="Tus incidencias abiertas" hidden />}

          {cerradas && (
            <div>
              <div style={{ padding: "8px 12px 2px", fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                letterSpacing: ".05em", color: "var(--text3)", borderTop: filtrados.length ? "1px solid var(--border2)" : "none" }}>
                Cerradas · últimos {cierres?.dias || 90} días
              </div>
              {cargandoC && <div style={vacio}>Consultando Odoo…</div>}
              {!cargandoC && falloC && <div style={vacio}>No se pudieron leer las incidencias cerradas</div>}
              {!cargandoC && !falloC && cierres && !cierres.tickets.length && (
                <div style={vacio}>
                  {q.trim() ? "Sin resultados entre las cerradas" : `No hay incidencias cerradas en los últimos ${cierres.dias} días`}
                </div>
              )}
              {listaCierres.length > 0 && (
                <div id={`${id}-ce`} role="listbox" aria-label="Incidencias cerradas">{listaCierres.map((x, i) => fila(x, filtrados.length + i))}</div>
              )}
              {!cargandoC && !falloC && cierres && cierres.total > cierres.tickets.length && (
                <div style={{ ...vacio, padding: "6px 12px 8px" }}>
                  Hay {cierres.total - cierres.tickets.length} más: afina la búsqueda.
                </div>
              )}
            </div>
          )}
          {!listaCierres.length && <div id={`${id}-ce`} role="listbox" aria-label="Incidencias cerradas" hidden />}
          {onAbrirPanel && !!(tickets || []).length && (
            <div onMouseDown={onAbrirPanel}
              style={{ padding: "6px 12px", cursor: "pointer", fontSize: 11,
                color: "var(--accent)", borderTop: "1px solid var(--border2)",
                display: "flex", alignItems: "center", gap: 5 }}>
              <Icon name="headset" size={10} color="var(--accent)" />Abrir el panel de incidencias
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const aviso = {
  padding: "7px 10px", borderRadius: 8, border: "1px dashed var(--border)",
  background: "var(--bg)", fontSize: 11, color: "var(--text3)",
  display: "flex", alignItems: "flex-start", gap: 6, lineHeight: 1.45,
};
const vacio = { padding: 12, fontSize: 12, color: "var(--text3)", textAlign: "center" };
